(ns common.permissions.entity-permissions
  (:require
    [common.permissions.field-permissions :as field-permissions]
    [common.roles :as roles]
    [common.schema.field-spec :as field-spec]
    [common.schema.registry :as registry]
    [potpuri.core :refer [fn-> fn->>]]))

(def CONFIGURABLE_ENTITIES
  [:entity.type/oppo
   :entity.type/fundraising-opportunity
   :entity.type/fund-commitment
   :entity.type/holding
   :entity.type/fund
   :entity.type/person
   :entity.type/company
   :entity.type/real-estate
   :entity.type/fof-asset
   :entity.type/other-asset
   :entity.type/investor
   :entity.type/sales-opportunity
   :entity.type/sale-agreement
   :entity.type/exit-opportunity
   :entity.type/activity
   :entity.type/value-creation
   :entity.type/risk
   :entity.type/esign-process
   :entity.type/risk-holding
   :entity.type/survey-profile
   :entity.type/survey-process
   :entity.type/metabase-dashboard
   :entity.type/advisory-board-decision
   :entity.type/aifm-decision
   :entity.type/ic-decision
   :entity.type/aifm-exit-decision
   :entity.type/ic-exit-decision
   :entity.type/decision-participant
   :entity.type/graph-person
   :entity.type/graph-company
   :entity.type/graph-fund
   :entity.type/graph-fof-asset
   :entity.type/survey-block
   :entity.type/survey-question
   :entity.type/survey-question-ext-id
   :entity.type/project-counterparty-diligence
   :entity.type/company-counterparty-diligence
   :entity.type/person-counterparty-diligence])

(def CONFIGURABLE-ENTITIES-SET
  (set CONFIGURABLE_ENTITIES))

;; explicit prevention of create-on-fly? for investors:
(def non-creatable
  #{:entity.type/investor})

;; first release for these (asset-types):
(def configurable-creator-entities
  #{:entity.type/company
    :entity.type/real-estate
    :entity.type/fof-asset
    :entity.type/other-asset
    ;; second release also these:
    :entity.type/person
    :entity.type/holding
    :entity.type/investor
    :entity.type/fund-commitment
    :entity.type/sale-agreement})

(def configurable-pipeline-creator-entities
  #{:entity.type/oppo})

(def custom-field-entity-types
  #{:entity.type/oppo
    :entity.type/fundraising-opportunity
    :entity.type/fund-commitment
    :entity.type/holding
    :entity.type/fund
    :entity.type/person
    :entity.type/company
    :entity.type/real-estate
    :entity.type/fof-asset
    :entity.type/other-asset
    :entity.type/investor
    :entity.type/sales-opportunity
    :entity.type/sale-agreement
    :entity.type/exit-opportunity})

(defn default-entity-permissions-map
  [tenant-schema
   active-teams
   whitelisted-fields
   blacklisted-fields]
  (->>
    CONFIGURABLE_ENTITIES
    (into
      {}
      (map
        (fn [entity-type]
          [entity-type
           (->>
             (registry/get-schema tenant-schema entity-type)
             (into
               {}
               (comp
                 (remove (fn [[_attribute fs]]
                           ;; attribute row is rendered
                           ;; field is not marked as not configured
                           ;; field can be read or written by user or admin
                           ;; field is not blocked by zadmin or default configuration
                           (let [min-zadmin-required (and (->> fs
                                                               :field/default-authorization
                                                               :access/read
                                                               (roles/zadmin?))
                                                          (->> fs
                                                               :field/default-authorization
                                                               :access/write
                                                               (roles/zadmin?)))]
                             min-zadmin-required)))
                 (map
                   (fn [[attribute _fs]]
                     (let [field-schema (registry/get-field-schema tenant-schema attribute)
                           default-auth (:field/default-authorization field-schema)
                           mandatory? (field-spec/system-required-or-legacy-always-mandatory-field? field-schema)
                           default-visibility (:field/default-visibility field-schema)
                           field-visibility (:field-visibility default-visibility)
                           default-user-access (roles/resolve-role-field-access :role/user default-auth)
                           default-ssg-setting (roles/resolve-role-field-access :role/guest-sell-side
                                                                                default-auth)
                           default-bsg-setting (roles/resolve-role-field-access :role/guest-buy-side
                                                                                default-auth)
                           in-use-value (cond
                                          (or mandatory? (= :in-use-disabled field-visibility))
                                          :in-use-disabled

                                          (contains? (set blacklisted-fields) attribute)
                                          :not-in-use-blocked

                                          (and (= field-visibility :not-in-use-blocked)
                                               (contains? (set whitelisted-fields) attribute))
                                          :not-in-use

                                          (= :not-in-use-blocked field-visibility)
                                          :not-in-use-blocked

                                          (= :in-use field-visibility)
                                          :in-use

                                          :else
                                          :not-in-use)
                           res (into {:field-settings/in-use-config   in-use-value
                                      :field-settings/buy-side-guest  default-bsg-setting
                                      :field-settings/sell-side-guest default-ssg-setting}
                                     (map (fn [t]
                                            (let [team-id (field-permissions/team-configuration-keyword t)]
                                              [team-id
                                               default-user-access])))
                                     active-teams)]
                       [attribute res]))))))])))))

(defn resolve-current-field-setting
  [attribute
   default-attr-config
   tenant-attr-config]
  (let [in-use-config (:field-settings/in-use-config default-attr-config)
        force-visibility-default? (contains? #{:not-in-use-blocked :in-use-disabled}
                                             in-use-config)]
    [attribute
     ;; default from schema
     (merge default-attr-config
            ;; tenant specified setting
            tenant-attr-config
            ;; enforce forced configuration
            (when force-visibility-default?
              {:field-settings/in-use-config in-use-config}))]))

(defn default-permissions
  [teams tenant tenant-schema]
  (let [whitelisted-fields (:tenant/whitelisted-custom-fields tenant)
        blacklisted-fields (:tenant/blacklisted-fields tenant)]
    (default-entity-permissions-map tenant-schema
                                    teams
                                    whitelisted-fields
                                    blacklisted-fields)))

(def remove-data-types
  #{:data.type/boolean
    :data.type/currency-symbol
    :data.type/text-edn
    :data.type/financials
    :data.type/tesi-financials
    :data.type/tesi-financials-2})

(defn available-webentry-fields
  [entity-type teams tenant-configs tenant tenant-schema]
  (let [defaults-with-in-use-information (default-permissions teams tenant tenant-schema)
        tenant-field-configurations
        (->> defaults-with-in-use-information
             (map (fn [[entity-type attr-configs]]
                    [entity-type
                     (->> attr-configs
                          (map (fn [[attr-key attr-config]]
                                 (let [tenant-field-configuration (get-in tenant-configs
                                                                          [entity-type attr-key])]
                                   (resolve-current-field-setting attr-key
                                                                  attr-config
                                                                  tenant-field-configuration))))
                          (into {}))]))
             (into {}))]
    (when entity-type
      (->> (get tenant-field-configurations entity-type)
           (filter (fn [[_attr conf]]
                     (some #{(:field-settings/in-use-config conf)}
                           field-permissions/POSITIVE-IN-USE-VALUES)))
           (map (fn [[attr _conf]]
                  [attr (registry/get-field-schema tenant-schema attr)]))
           (remove (fn-> second :field/hide-from-webform?))
           (remove (fn-> second :field/hidden-from-configuration?))
           (remove (fn-> second :field/calculated-field))
           (remove (fn-> second :synthetic?))
           (filter (fn->> second
                          :field/default-authorization
                          (roles/resolve-role-field-access :role/admin)
                          field-permissions/write-access?))
           (remove (fn-> second :field/data-type remove-data-types))
           (remove
             (fn [[_attr {:keys [field/data-type named-value-attribute] :as _fs}]]
               ;;filter out refs so tenant's entire [entity-type] universe is not exposed accidentally:
               (and (some #{data-type} [:data.type/ref-multi :data.type/ref-single :data.type/id])
                    ;;unless it's a named-value:
                    (not named-value-attribute))))
           (sort-by (fn [[_attr fs]]
                      (-> fs
                          :field/default-order)))
           (map first)))))
